import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/inherits";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }
function _isNativeReflectConstruct() { try { var t = !Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); } catch (t) {} return (_isNativeReflectConstruct = function _isNativeReflectConstruct() { return !!t; })(); }
/** @jsx jsx */
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { css, jsx } from '@emotion/react';
import classnames from 'classnames';
import { injectIntl } from 'react-intl-next';
import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/custom-theme-button';
import { useSharedPluginState } from '@atlaskit/editor-common/hooks';
import { GRID_GUTTER } from '@atlaskit/editor-common/styles';
import { WidthConsumer } from '@atlaskit/editor-common/ui';
import { ToolbarArrowKeyNavigationProvider } from '@atlaskit/editor-common/ui-menu';
import { tableCommentEditorStyles } from '@atlaskit/editor-plugins/table/ui/common-styles';
import { akEditorMobileBreakoutPoint } from '@atlaskit/editor-shared-styles';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';
import { N100, N40 } from '@atlaskit/theme/colors';
import { borderRadius } from '@atlaskit/theme/constants';
import messages from '../../../messages';
import { usePresetContext } from '../../../presets/context';
import { ClickAreaBlock } from '../../Addon';
import { createEditorContentStyle } from '../../ContentStyles';
import PluginSlot from '../../PluginSlot';
import Toolbar from '../../Toolbar';
import WithFlash from '../../WithFlash';
import { MainToolbar, mainToolbarCustomComponentsSlotStyle } from './Toolbar';
var CommentEditorMargin = 14;
var commentEditorStyles = css({
  display: 'flex',
  flexDirection: 'column',
  '.less-margin .ProseMirror': {
    margin: "var(--ds-space-150, 12px)".concat(" ", "var(--ds-space-100, 8px)", " ", "var(--ds-space-100, 8px)")
  },
  minWidth: '272px',
  height: 'auto',
  backgroundColor: "var(--ds-background-input, white)",
  border: "1px solid ".concat(getBooleanFF('platform.design-system-team.border-checkbox_nyoiu') ? "var(--ds-border-input, ".concat(N100, ")") : "var(--ds-border, ".concat(N40, ")")),
  boxSizing: 'border-box',
  borderRadius: "".concat(borderRadius(), "px"),
  maxWidth: 'inherit',
  wordWrap: 'break-word'
});
var ContentArea = createEditorContentStyle(css({
  flexGrow: 1,
  overflowX: getBooleanFF('platform.editor.table-sticky-scrollbar') ? 'clip' : 'hidden',
  lineHeight: '24px',
  '.ProseMirror': {
    margin: "var(--ds-space-150, 12px)"
  },
  '.gridParent': {
    marginLeft: "var(--ds-space-025, 2px)",
    marginRight: "var(--ds-space-025, 2px)",
    width: "calc(100% + ".concat(CommentEditorMargin - GRID_GUTTER, "px)")
  },
  padding: "var(--ds-space-250, 20px)"
}, tableCommentEditorStyles));
ContentArea.displayName = 'ContentArea';
var secondaryToolbarStyles = css({
  boxSizing: 'border-box',
  justifyContent: 'flex-end',
  alignItems: 'center',
  display: 'flex',
  padding: "var(--ds-space-150, 12px)".concat(" ", "var(--ds-space-025, 2px)")
});
var appearance = 'comment';
var Editor = /*#__PURE__*/function (_React$Component) {
  _inherits(Editor, _React$Component);
  var _super = _createSuper(Editor);
  function Editor(props) {
    var _this;
    _classCallCheck(this, Editor);
    _this = _super.call(this, props);
    _defineProperty(_assertThisInitialized(_this), "appearance", 'comment');
    _defineProperty(_assertThisInitialized(_this), "containerElement", null);
    // Wrapper container for toolbar and content area
    _defineProperty(_assertThisInitialized(_this), "wrapperElementRef", /*#__PURE__*/React.createRef());
    _defineProperty(_assertThisInitialized(_this), "handleSave", function () {
      if (_this.props.editorView && _this.props.onSave) {
        _this.props.onSave(_this.props.editorView);
      }
    });
    _defineProperty(_assertThisInitialized(_this), "handleCancel", function () {
      if (_this.props.editorView && _this.props.onCancel) {
        _this.props.onCancel(_this.props.editorView);
      }
    });
    _defineProperty(_assertThisInitialized(_this), "renderChrome", function (_ref) {
      var maxContentSize = _ref.maxContentSize,
        mediaState = _ref.mediaState;
      var _this$props = _this.props,
        editorDOMElement = _this$props.editorDOMElement,
        editorView = _this$props.editorView,
        editorActions = _this$props.editorActions,
        eventDispatcher = _this$props.eventDispatcher,
        providerFactory = _this$props.providerFactory,
        contentComponents = _this$props.contentComponents,
        customContentComponents = _this$props.customContentComponents,
        customPrimaryToolbarComponents = _this$props.customPrimaryToolbarComponents,
        primaryToolbarComponents = _this$props.primaryToolbarComponents,
        customSecondaryToolbarComponents = _this$props.customSecondaryToolbarComponents,
        popupsMountPoint = _this$props.popupsMountPoint,
        popupsBoundariesElement = _this$props.popupsBoundariesElement,
        popupsScrollableElement = _this$props.popupsScrollableElement,
        maxHeight = _this$props.maxHeight,
        _this$props$minHeight = _this$props.minHeight,
        minHeight = _this$props$minHeight === void 0 ? 150 : _this$props$minHeight,
        onSave = _this$props.onSave,
        onCancel = _this$props.onCancel,
        disabled = _this$props.disabled,
        dispatchAnalyticsEvent = _this$props.dispatchAnalyticsEvent,
        intl = _this$props.intl,
        useStickyToolbar = _this$props.useStickyToolbar,
        pluginHooks = _this$props.pluginHooks,
        featureFlags = _this$props.featureFlags;
      var maxContentSizeReached = Boolean(maxContentSize === null || maxContentSize === void 0 ? void 0 : maxContentSize.maxContentSizeReached);
      var showSecondaryToolbar = !!onSave || !!onCancel || !!customSecondaryToolbarComponents;
      var isShortcutToFocusToolbar = function isShortcutToFocusToolbar(event) {
        //Alt + F9 to reach first element in this main toolbar
        return event.altKey && (event.key === 'F9' || event.keyCode === 120);
      };
      var isTwoLineToolbarEnabled = !!customPrimaryToolbarComponents && !!(featureFlags !== null && featureFlags !== void 0 && featureFlags.twoLineEditorToolbar);
      var handleEscape = function handleEscape(event) {
        if (!(editorView !== null && editorView !== void 0 && editorView.hasFocus())) {
          editorView === null || editorView === void 0 || editorView.focus();
        }
        event.preventDefault();
        event.stopPropagation();
      };
      return jsx(WithFlash, {
        animate: maxContentSizeReached
      }, jsx("div", {
        css: [commentEditorStyles, css({
          minHeight: "".concat(minHeight, "px")
        })],
        className: "akEditor",
        ref: _this.wrapperElementRef
      }, jsx(MainToolbar, {
        useStickyToolbar: useStickyToolbar,
        twoLineEditorToolbar: isTwoLineToolbarEnabled
      }, jsx(ToolbarArrowKeyNavigationProvider, {
        editorView: editorView,
        childComponentSelector: "[data-testid='ak-editor-main-toolbar']",
        isShortcutToFocusToolbar: isShortcutToFocusToolbar,
        handleEscape: handleEscape,
        editorAppearance: _this.appearance,
        useStickyToolbar: useStickyToolbar,
        intl: intl
      }, jsx(Toolbar, {
        editorView: editorView,
        editorActions: editorActions,
        eventDispatcher: eventDispatcher,
        providerFactory: providerFactory,
        appearance: _this.appearance,
        items: primaryToolbarComponents,
        popupsMountPoint: popupsMountPoint,
        popupsBoundariesElement: popupsBoundariesElement,
        popupsScrollableElement: popupsScrollableElement,
        disabled: !!disabled,
        dispatchAnalyticsEvent: dispatchAnalyticsEvent,
        containerElement: _this.containerElement,
        twoLineEditorToolbar: isTwoLineToolbarEnabled
      }), jsx("div", {
        css: mainToolbarCustomComponentsSlotStyle(isTwoLineToolbarEnabled)
      }, customPrimaryToolbarComponents))), jsx(ClickAreaBlock, {
        editorView: editorView,
        editorDisabled: disabled
      }, jsx(WidthConsumer, null, function (_ref2) {
        var width = _ref2.width;
        return jsx(ContentArea, {
          ref: function ref(_ref3) {
            return _this.containerElement = _ref3;
          },
          css: maxHeight ? css({
            maxHeight: "".concat(maxHeight, "px")
          }) : null,
          className: classnames('ak-editor-content-area', {
            'less-margin': width < akEditorMobileBreakoutPoint
          }),
          featureFlags: featureFlags
        }, customContentComponents && 'before' in customContentComponents ? customContentComponents.before : customContentComponents, jsx(PluginSlot, {
          editorView: editorView,
          editorActions: editorActions,
          eventDispatcher: eventDispatcher,
          dispatchAnalyticsEvent: dispatchAnalyticsEvent,
          providerFactory: providerFactory,
          appearance: _this.appearance,
          items: contentComponents,
          popupsMountPoint: popupsMountPoint,
          popupsBoundariesElement: popupsBoundariesElement,
          popupsScrollableElement: popupsScrollableElement,
          containerElement: _this.containerElement,
          disabled: !!disabled,
          wrapperElement: _this.wrapperElementRef.current,
          pluginHooks: pluginHooks
        }), editorDOMElement, customContentComponents && 'after' in customContentComponents ? customContentComponents.after : null);
      }))), showSecondaryToolbar && jsx("div", {
        css: secondaryToolbarStyles,
        "data-testid": "ak-editor-secondary-toolbar"
      }, jsx(ButtonGroup, null, !!onSave && jsx(Button, {
        appearance: "primary",
        onClick: _this.handleSave,
        testId: "comment-save-button",
        isDisabled: disabled || mediaState && !mediaState.allUploadsFinished
      }, intl.formatMessage(messages.saveButton)), !!onCancel && jsx(Button, {
        appearance: "subtle",
        onClick: _this.handleCancel,
        isDisabled: disabled
      }, intl.formatMessage(messages.cancelButton))), jsx("span", {
        style: {
          flexGrow: 1
        }
      }), customSecondaryToolbarComponents));
    });
    if (props.innerRef) {
      _this.wrapperElementRef = props.innerRef;
    }
    return _this;
  }
  _createClass(Editor, [{
    key: "render",
    value: function render() {
      return jsx(RenderWithPluginState, {
        renderChrome: this.renderChrome
      });
    }
  }]);
  return Editor;
}(React.Component);
_defineProperty(Editor, "displayName", 'CommentEditorAppearance');
function RenderWithPluginState(_ref4) {
  var renderChrome = _ref4.renderChrome;
  var api = usePresetContext();
  var _useSharedPluginState = useSharedPluginState(api, ['media', 'maxContentSize']),
    mediaState = _useSharedPluginState.mediaState,
    maxContentSizeState = _useSharedPluginState.maxContentSizeState;
  return jsx(Fragment, null, renderChrome({
    maxContentSize: maxContentSizeState,
    mediaState: mediaState !== null && mediaState !== void 0 ? mediaState : undefined
  }));
}
var EditorNext = function EditorNext(props) {
  var api = usePresetContext();
  var _useSharedPluginState2 = useSharedPluginState(api, ['media', 'maxContentSize']),
    mediaState = _useSharedPluginState2.mediaState,
    maxContentSizeState = _useSharedPluginState2.maxContentSizeState;
  var editorDOMElement = props.editorDOMElement,
    editorView = props.editorView,
    editorActions = props.editorActions,
    eventDispatcher = props.eventDispatcher,
    providerFactory = props.providerFactory,
    contentComponents = props.contentComponents,
    customContentComponents = props.customContentComponents,
    customPrimaryToolbarComponents = props.customPrimaryToolbarComponents,
    primaryToolbarComponents = props.primaryToolbarComponents,
    customSecondaryToolbarComponents = props.customSecondaryToolbarComponents,
    popupsMountPoint = props.popupsMountPoint,
    popupsBoundariesElement = props.popupsBoundariesElement,
    popupsScrollableElement = props.popupsScrollableElement,
    maxHeight = props.maxHeight,
    _props$minHeight = props.minHeight,
    minHeight = _props$minHeight === void 0 ? 150 : _props$minHeight,
    onSave = props.onSave,
    onCancel = props.onCancel,
    disabled = props.disabled,
    dispatchAnalyticsEvent = props.dispatchAnalyticsEvent,
    intl = props.intl,
    useStickyToolbar = props.useStickyToolbar,
    pluginHooks = props.pluginHooks,
    featureFlags = props.featureFlags,
    innerRef = props.innerRef;
  var maxContentSizeReached = Boolean(maxContentSizeState === null || maxContentSizeState === void 0 ? void 0 : maxContentSizeState.maxContentSizeReached);
  var showSecondaryToolbar = !!onSave || !!onCancel || !!customSecondaryToolbarComponents;
  var containerElement = null;

  // Wrapper container for toolbar and content area
  var wrapperElementRef = useMemo(function () {
    return innerRef || /*#__PURE__*/React.createRef();
  }, [innerRef]);
  var _useState = useState(disabled),
    _useState2 = _slicedToArray(_useState, 2),
    saveButtonDisabled = _useState2[0],
    setSaveButtonDisabled = _useState2[1];
  useEffect(function () {
    if (mediaState) {
      mediaState.subscribeToUploadInProgressState(setSaveButtonDisabled);
    }
    return function () {
      return mediaState === null || mediaState === void 0 ? void 0 : mediaState.unsubscribeFromUploadInProgressState(setSaveButtonDisabled);
    };
  }, [mediaState]);
  var handleSave = useCallback(function () {
    if (editorView && onSave) {
      onSave(editorView);
    }
  }, [editorView, onSave]);
  var handleCancel = useCallback(function () {
    if (editorView && onCancel) {
      onCancel(editorView);
    }
  }, [editorView, onCancel]);
  var isShortcutToFocusToolbar = useCallback(function (event) {
    //Alt + F9 to reach first element in this main toolbar
    return event.altKey && (event.key === 'F9' || event.keyCode === 120);
  }, []);
  var isTwoLineToolbarEnabled = !!customPrimaryToolbarComponents && !!(featureFlags !== null && featureFlags !== void 0 && featureFlags.twoLineEditorToolbar);
  var handleEscape = useCallback(function (event) {
    if (!(editorView !== null && editorView !== void 0 && editorView.hasFocus())) {
      editorView === null || editorView === void 0 || editorView.focus();
    }
    event.preventDefault();
    event.stopPropagation();
  }, [editorView]);
  return jsx(WithFlash, {
    animate: maxContentSizeReached
  }, jsx("div", {
    css: [commentEditorStyles, css({
      minHeight: "".concat(minHeight, "px")
    })],
    className: "akEditor",
    ref: wrapperElementRef
  }, jsx(MainToolbar, {
    useStickyToolbar: useStickyToolbar,
    twoLineEditorToolbar: isTwoLineToolbarEnabled
  }, jsx(ToolbarArrowKeyNavigationProvider, {
    editorView: editorView,
    childComponentSelector: "[data-testid='ak-editor-main-toolbar']",
    isShortcutToFocusToolbar: isShortcutToFocusToolbar,
    handleEscape: handleEscape,
    editorAppearance: appearance,
    useStickyToolbar: useStickyToolbar,
    intl: intl
  }, jsx(Toolbar, {
    editorView: editorView,
    editorActions: editorActions,
    eventDispatcher: eventDispatcher,
    providerFactory: providerFactory,
    appearance: appearance,
    items: primaryToolbarComponents,
    popupsMountPoint: popupsMountPoint,
    popupsBoundariesElement: popupsBoundariesElement,
    popupsScrollableElement: popupsScrollableElement,
    disabled: !!disabled,
    dispatchAnalyticsEvent: dispatchAnalyticsEvent,
    containerElement: containerElement,
    twoLineEditorToolbar: isTwoLineToolbarEnabled
  }), jsx("div", {
    css: mainToolbarCustomComponentsSlotStyle(isTwoLineToolbarEnabled)
  }, customPrimaryToolbarComponents))), jsx(ClickAreaBlock, {
    editorView: editorView,
    editorDisabled: disabled
  }, jsx(WidthConsumer, null, function (_ref5) {
    var width = _ref5.width;
    return jsx(ContentArea, {
      ref: function ref(_ref6) {
        return containerElement = _ref6;
      },
      css: maxHeight ? css({
        maxHeight: "".concat(maxHeight, "px")
      }) : null,
      className: classnames('ak-editor-content-area', {
        'less-margin': width < akEditorMobileBreakoutPoint
      }),
      featureFlags: featureFlags
    }, customContentComponents && 'before' in customContentComponents ? customContentComponents.before : customContentComponents, jsx(PluginSlot, {
      editorView: editorView,
      editorActions: editorActions,
      eventDispatcher: eventDispatcher,
      dispatchAnalyticsEvent: dispatchAnalyticsEvent,
      providerFactory: providerFactory,
      appearance: appearance,
      items: contentComponents,
      popupsMountPoint: popupsMountPoint,
      popupsBoundariesElement: popupsBoundariesElement,
      popupsScrollableElement: popupsScrollableElement,
      containerElement: containerElement,
      disabled: !!disabled,
      wrapperElement: wrapperElementRef.current,
      pluginHooks: pluginHooks
    }), editorDOMElement, customContentComponents && 'after' in customContentComponents ? customContentComponents.after : null);
  }))), showSecondaryToolbar && jsx("div", {
    css: secondaryToolbarStyles,
    "data-testid": "ak-editor-secondary-toolbar"
  }, jsx(ButtonGroup, null, !!onSave && jsx(Button, {
    appearance: "primary",
    onClick: handleSave,
    testId: "comment-save-button",
    isDisabled: disabled || saveButtonDisabled
  }, intl.formatMessage(messages.saveButton)), !!onCancel && jsx(Button, {
    appearance: "subtle",
    onClick: handleCancel,
    isDisabled: disabled
  }, intl.formatMessage(messages.cancelButton))), jsx("span", {
    style: {
      flexGrow: 1
    }
  }), customSecondaryToolbarComponents));
};
EditorNext.displayName = 'CommentEditorAppearance';
var CommentEditorNextWithIntl = injectIntl(EditorNext);
var CommentEditorOldWithIntl = injectIntl(Editor);
var ExportComp = getBooleanFF('platform.editor.media.alluploadsfinished-dispatch-update_ivtow') ? CommentEditorNextWithIntl : CommentEditorOldWithIntl;
export var CommentEditorWithIntl = ExportComp;